/**
 * Order stage model for the redesigned order experience.
 *
 * The redesign (see references/live-project) presents order status as an 11-stage
 * tracker. Our backend currently reports a smaller set of statuses via
 * `approval_status_name` / `approval_status_slug`, so `mapToOrderStage()` translates
 * what we have onto the full visual flow. Stages we don't yet receive from the
 * backend simply never light up until the API exposes them.
 */

export type OrderStage =
  | 'draft'
  | 'estimate'
  | 'under_review'
  | 'art_department'
  | 'in_production'
  | 'finishing'
  | 'packing'
  | 'ready_to_ship'
  | 'shipped'
  | 'delivered'
  | 'invoiced'
  | 'on_hold';

export const ORDER_STAGE_LABELS: Record<OrderStage, string> = {
  draft: 'Draft',
  estimate: 'Estimate',
  under_review: 'Under Review',
  art_department: 'Art Department',
  in_production: 'Production',
  finishing: 'Finishing',
  packing: 'Packing',
  ready_to_ship: 'Ready to Ship',
  shipped: 'Shipped',
  delivered: 'Delivered',
  invoiced: 'Invoiced',
  on_hold: 'On Hold'
};

/** Linear progression of stages. `on_hold` is a side state and is excluded. */
export const ORDER_STAGE_FLOW: OrderStage[] = [
  'draft',
  'estimate',
  'under_review',
  'art_department',
  'in_production',
  'finishing',
  'packing',
  'ready_to_ship',
  'shipped',
  'delivered',
  'invoiced'
];

/**
 * Stages shown in the Orders-home pipeline (Story 11.2).
 *
 * The prototype's pipeline runs under_review → shipped (7 stages), but our
 * backend only reports statuses that map to these five. The prototype's
 * finishing / packing / ready_to_ship stages aren't exposed yet, and completed
 * orders (Payment/Fulfilled) map to `invoiced`. See docs/redesign-divergences.md.
 */
export const ORDER_PIPELINE_FLOW: OrderStage[] = [
  'under_review',
  'art_department',
  'in_production',
  'finishing',
  'packing',
  'ready_to_ship',
  'shipped'
];

/** Statuses selected by default on the Orders table (estimates + the active production pipeline). */
export const DEFAULT_ORDER_STATUS_STAGES: OrderStage[] = [
  'estimate',
  'art_department',
  'in_production',
  'finishing',
  'packing',
  'ready_to_ship'
];

/**
 * Join a set of stages into the backend `status` query-param value. The OrderStage
 * keys are already the lowercase snake_case tokens the backend expects, so they're
 * sent as-is (e.g. `?status=art_department,in_production,ready_to_ship`).
 */
export function stagesToStatusParam(stages: OrderStage[]): string {
  return (stages ?? []).join(',');
}

/**
 * Response shape of the pipeline stage-counts endpoint
 * (`cp/orders/:client_id/stage-counts`): a count per stage token plus `total`.
 * Keys mostly match the OrderStage tokens, except the backend reports
 * `production` where our model uses `in_production` — bridge with `stageCountKey()`.
 */
export interface OrderStageCounts {
  total?: number;
  [stageToken: string]: number;
}

/**
 * The key under which the stage-counts endpoint reports a given pipeline stage.
 * Identical to the OrderStage token for every stage except `in_production`, which
 * the backend reports as `production`.
 */
export function stageCountKey(stage: OrderStage): string {
  return stage === 'in_production' ? 'production' : stage;
}

/** A pipeline stage decorated with its label/icon/color and current order count. */
export interface PipelineStageCount {
  stage: OrderStage;
  label: string;
  icon: string;
  color: string;
  count: number;
}

/**
 * Build the Orders-home pipeline stages (Story 11.2 / CS-3428) from a stage-counts
 * response. Always returns every stage in {@link ORDER_PIPELINE_FLOW} in order;
 * missing/blank counts default to 0. Bridges the backend's `production` key onto
 * our `in_production` stage via {@link stageCountKey}.
 */
export function pipelineStagesFromCounts(counts?: OrderStageCounts | null): PipelineStageCount[] {
  const safe = counts ?? {};
  return ORDER_PIPELINE_FLOW.map((stage) => ({
    stage,
    label: ORDER_STAGE_LABELS[stage],
    icon: ORDER_STAGE_META[stage].icon,
    color: ORDER_STAGE_META[stage].color,
    count: Number(safe[stageCountKey(stage)]) || 0
  }));
}

export interface OrderStageMeta {
  /** FontAwesome icon class (the app ships FontAwesome, not lucide). */
  icon: string;
  /** Badge background color. */
  color: string;
}

export const ORDER_STAGE_META: Record<OrderStage, OrderStageMeta> = {
  draft: { icon: 'fa-file-lines', color: '#64748b' },
  estimate: { icon: 'fa-file-invoice-dollar', color: '#e37b3a' },
  under_review: { icon: 'fa-clock', color: '#d97f1e' },
  art_department: { icon: 'fa-palette', color: '#8001ea' },
  in_production: { icon: 'fa-box', color: '#2563eb' },
  finishing: { icon: 'fa-scissors', color: '#7c3aed' },
  packing: { icon: 'fa-box-open', color: '#0ea5e9' },
  ready_to_ship: { icon: 'fa-clipboard-check', color: '#0d9488' },
  shipped: { icon: 'fa-paper-plane', color: '#1d44fa' },
  delivered: { icon: 'fa-truck', color: '#28c76f' },
  invoiced: { icon: 'fa-receipt', color: '#147a4a' },
  on_hold: { icon: 'fa-circle-pause', color: '#e53e3e' }
};

/**
 * Authoritative mapping from the orders API's granular status onto an OrderStage.
 *
 * The granular status arrives as `approval_id.value` on the list endpoint
 * (e.g. "Sales Review", "Screen Room", "On Press - In Production") and as
 * `raw_approval_status_name` on the V2 order-detail endpoint — that payload's
 * `approval_status_name` is the coarse CP label, not the granular status.
 * Keys here are those strings
 * normalized by `normalizeStatus()` (lower-cased, all non-alphanumerics
 * stripped), so matching is case-insensitive and tolerant of spacing /
 * punctuation differences — "out w/Third Party", "On Press - In Production"
 * and "EMBROIDERY DEPARTMENT" all match regardless of how they're punctuated.
 *
 * Source of truth (CS-3428): the status → mapped-status table provided by CS.
 * A few keys carry both the spec's wording and the actual API value where they
 * differ (e.g. spec "Estimate" is API "Order-Estimate"). Keep in sync with the spec.
 */
const STATUS_STAGE_MAP: Record<string, OrderStage> = {
  // Estimate
  estimate: 'estimate',
  orderestimate: 'estimate',       // actual approval_id.value
  // Under Review
  incoming: 'under_review',
  orderincoming: 'under_review',
  orderack: 'under_review',
  orderacknowledgement: 'under_review', // actual approval_id.value
  salesreview: 'under_review',
  // Art Department
  art: 'art_department',
  artreview: 'art_department',
  artdepartment: 'art_department',
  // Production
  production: 'in_production',
  inproduction: 'in_production',
  screenroom: 'in_production',
  productiondept: 'in_production',
  outwthirdparty: 'in_production',
  onpressinproduction: 'in_production',
  embroiderydepartment: 'in_production',
  dtgdepartment: 'in_production',
  // Finishing
  finishing: 'finishing',
  finishingdept: 'finishing',
  sewing: 'finishing',
  // Packing
  packing: 'packing',
  shippingpacking: 'packing',
  // Ready to Ship
  readytoship: 'ready_to_ship',
  // Shipped
  shipped: 'shipped',
  shippedpickedup: 'shipped',
  // Invoiced
  payment: 'invoiced',
  orderfinalinvoice: 'invoiced',
  orderpaidinfull: 'invoiced',
  fulfilled: 'invoiced',
};

/**
 * Fallback keyed on the backend's coarse client-portal label
 * (`approval_id.cpstatus.label`): Estimate, Art, In Production, Shipped,
 * Payment, Fulfilled. Used when the granular status isn't in STATUS_STAGE_MAP
 * so real orders still bucket sensibly instead of dropping to Draft.
 */
const CP_LABEL_STAGE_MAP: Record<string, OrderStage> = {
  estimate: 'estimate',
  art: 'art_department',
  inproduction: 'in_production',
  shipped: 'shipped',
  payment: 'invoiced',
  fulfilled: 'invoiced'
};

/** Lower-cases and strips spaces / special characters so matching is lenient. */
function normalizeStatus(value: any): string {
  return (value ?? '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Maps our backend order status onto an OrderStage.
 *
 * Tries the granular status first (`approval_id.value` on the list,
 * `raw_approval_status_name` on the V2 order detail, or `approval_status_name`
 * / `status` on older payloads) against the CS-3428 table, then falls back to
 * the coarse `cpstatus.label`. Anything still unrecognized (e.g. genuine
 * drafts with no approval) maps to `draft`.
 */
export function mapToOrderStage(order: any): OrderStage {
  if (!order) {
    return 'draft';
  }

  const approval = order.approval_id ?? order;

  // Primary: the granular internal status.
  const primary = normalizeStatus(
    approval.value ?? order.value ?? order.raw_approval_status_name ?? order.approval_status_name ?? order.status
  );
  if (STATUS_STAGE_MAP[primary]) { return STATUS_STAGE_MAP[primary]; }
  if (CP_LABEL_STAGE_MAP[primary]) { return CP_LABEL_STAGE_MAP[primary]; }

  // Fallback: the coarse client-portal label the backend already computed.
  const coarse = normalizeStatus(approval.cpstatus?.label ?? order.cpstatus?.label);
  return CP_LABEL_STAGE_MAP[coarse] ?? 'draft';
}
